import { useParseMeta } from "@2u-landing-pages/providers";
import { renderEmbeddedEntry, hyperLinkRenderer, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Card as BricksCard } from "@red-digital/bricks";
import cn from "classnames";
import { forwardRef } from "react";
import { ContentfulImage } from "../../../ContentfulImage";
import { CardVariantProps } from "../../Card.type";
// @ts-ignore
import cardStyles from "../../Card.module.scss";
// @ts-ignore
import { ContentfulImageConfig } from "@2u-landing-pages/types";
import mediumCardStyles from "./MediumCard.module.scss";

export const MediumCard = forwardRef<HTMLDivElement, CardVariantProps>(({
  entry,
  heading,
  body,
  className,
  style,
  phoneNumber,
  inspector,
}, ref) => {
  const parseMetadata = useParseMeta();
  const { image, image_config, heading: contentfulHeading, body: contentfulBody } = entry.fields;

  const imageConfig = image_config as unknown as ContentfulImageConfig;
  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;
  const isSvgImage = image?.fields?.file?.contentType === "image/svg+xml";

  return (
    <BricksCard
      ref={ref}
      style={style}
      className={cn(cardStyles.card, mediumCardStyles.medium, className, {
        [cardStyles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
        [cardStyles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
      })}
      layout={{
        direction: "horizontal",
        breakpoint: "lg",
      }}
      media={
        image?.fields?.file?.url
          ? {
              component: (
                <ContentfulImage
                  image={image}
                  config={image_config}
                  className={cn({ [cardStyles.svgImage]: isSvgImage })}
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "image",
                  })}
                />
              ),
              position: "beforeContent",
            }
          : undefined
      }
      content={{
        headingProps: {
          variant: "heading-xl",
          className: cardStyles.heading,
          ...heading,
          children: contentfulHeading
            ? documentToReactComponents(contentfulHeading, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(
                      node,
                      "Card Medium Heading",
                      contentfulHeading,
                      phoneNumber,
                      parseMetadata,
                      "md"
                    ),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "heading",
          }),
        },
        descriptionProps: {
          modifier: 500,
          variant: "body-lg",
          className: cardStyles.body,
          ...body,
          children: contentfulBody
            ? documentToReactComponents(contentfulBody, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Medium Body", contentfulBody, phoneNumber, parseMetadata),
                  [INLINES.HYPERLINK]: (node) => hyperLinkRenderer(node, "Card Medium - Body"),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "body",
          }),
        },
      }}
    />
  );
});

MediumCard.displayName = "MediumCard";